import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import achievementsApi, { type UserAchievement, type AchievementStats } from '../../services/achievementsApi';
import './AchievementsPage.css';

const AchievementsPage: React.FC = () => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<UserAchievement[]>([]);
  const [stats, setStats] = useState<AchievementStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [selectedAchievement, setSelectedAchievement] = useState<UserAchievement | null>(null);

  const categories = [
    { id: 'all', name: 'All', icon: '🎯' },
    { id: 'gameplay', name: 'Gameplay', icon: '🎮' },
    { id: 'competitive', name: 'Competitive', icon: '🏆' },
    { id: 'social', name: 'Social', icon: '👥' },
    { id: 'progression', name: 'Progression', icon: '📈' },
    { id: 'special', name: 'Special', icon: '✨' },
  ];

  const tiers = [
    { id: 'all', name: 'All Tiers', color: '#888' },
    { id: 'bronze', name: 'Bronze', color: '#CD7F32' },
    { id: 'silver', name: 'Silver', color: '#C0C0C0' },
    { id: 'gold', name: 'Gold', color: '#FFD700' },
    { id: 'platinum', name: 'Platinum', color: '#E5E4E2' },
  ];

  useEffect(() => {
    loadAchievements();
  }, [user]);

  const loadAchievements = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [achievementsData, statsData] = await Promise.all([
        achievementsApi.getUserAchievements(user.id),
        achievementsApi.getUserStats(user.id),
      ]);

      setAchievements(achievementsData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load achievements');
      console.error('Error loading achievements:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAchievements = achievements.filter((achievement) => {
    const categoryMatch = selectedCategory === 'all' || achievement.achievement.category === selectedCategory;
    const tierMatch = selectedTier === 'all' || achievement.achievement.tier === selectedTier;
    return categoryMatch && tierMatch;
  });

  const getTierColor = (tier: string): string => {
    const tierObj = tiers.find((t) => t.id === tier);
    return tierObj?.color || '#888';
  };

  const getTierGradient = (tier: string): string => {
    switch (tier) {
      case 'bronze':
        return 'linear-gradient(135deg, rgba(205, 127, 50, 0.2) 0%, rgba(139, 69, 19, 0.2) 100%)';
      case 'silver':
        return 'linear-gradient(135deg, rgba(192, 192, 192, 0.2) 0%, rgba(128, 128, 128, 0.2) 100%)';
      case 'gold':
        return 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(218, 165, 32, 0.2) 100%)';
      case 'platinum':
        return 'linear-gradient(135deg, rgba(229, 228, 226, 0.2) 0%, rgba(169, 169, 169, 0.2) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)';
    }
  };

  if (loading) {
    return (
      <div className="achievements-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading achievements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievements-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadAchievements} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="achievements-page">
      {/* Header */}
      <div className="achievements-header">
        <div className="header-content">
          <h1>🏆 Achievements</h1>
          <p>Unlock achievements by completing challenges and reaching milestones</p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="stats-overview">
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-info">
                <div className="stat-value">{stats.completed}/{stats.totalAvailable}</div>
                <div className="stat-label">Unlocked</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">{stats.completionRate}%</div>
                <div className="stat-label">Completion</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <div className="stat-value">{stats.totalPoints}</div>
                <div className="stat-label">Points</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔄</div>
              <div className="stat-info">
                <div className="stat-value">{stats.inProgress}</div>
                <div className="stat-label">In Progress</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Category Filter */}
      <div className="category-filter">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            <span className="category-icon">{category.icon}</span>
            <span className="category-name">{category.name}</span>
          </button>
        ))}
      </div>

      {/* Tier Filter */}
      <div className="tier-filter">
        {tiers.map((tier) => (
          <button
            key={tier.id}
            className={`tier-button ${selectedTier === tier.id ? 'active' : ''}`}
            onClick={() => setSelectedTier(tier.id)}
            style={
              selectedTier === tier.id
                ? { borderColor: tier.color, boxShadow: `0 0 20px ${tier.color}40` }
                : {}
            }
          >
            {tier.name}
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      <div className="achievements-grid">
        {filteredAchievements.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎯</div>
            <h3>No achievements found</h3>
            <p>Try selecting a different category or tier</p>
          </div>
        ) : (
          filteredAchievements.map((userAchievement) => {
            const achievement = userAchievement.achievement;
            const isLocked = !userAchievement.completed;

            return (
              <div
                key={achievement.id}
                className={`achievement-card ${isLocked ? 'locked' : 'unlocked'} tier-${achievement.tier}`}
                style={{ background: getTierGradient(achievement.tier) }}
                onClick={() => setSelectedAchievement(userAchievement)}
              >
                {/* Tier Badge */}
                <div className="tier-badge" style={{ color: getTierColor(achievement.tier) }}>
                  {achievement.tier}
                </div>

                {/* Achievement Icon */}
                <div className={`achievement-icon ${isLocked ? 'locked-icon' : ''}`}>
                  {isLocked ? '🔒' : achievement.icon}
                </div>

                {/* Achievement Info */}
                <div className="achievement-info">
                  <h3 className="achievement-name">
                    {achievement.name}
                    {isLocked && <span className="locked-indicator"> 🔒</span>}
                  </h3>
                  <p className="achievement-description">
                    {achievement.description}
                    {isLocked && <span className="locked-hint"> (Not yet unlocked)</span>}
                  </p>
                </div>

                {/* Progress Bar */}
                {isLocked && userAchievement.progress > 0 && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${userAchievement.progressPercentage}%`,
                          background: `linear-gradient(90deg, ${getTierColor(achievement.tier)} 0%, ${getTierColor(achievement.tier)}80 100%)`,
                        }}
                      ></div>
                    </div>
                    <div className="progress-text">
                      {userAchievement.progress}/{userAchievement.target}
                    </div>
                  </div>
                )}

                {/* Points Badge */}
                <div className={`points-badge ${isLocked ? 'locked-points' : ''}`}>
                  <span className="points-icon">⭐</span>
                  <span className="points-value">{achievement.points}</span>
                </div>

                {/* Unlocked Date */}
                {!isLocked && userAchievement.unlockedAt && (
                  <div className="unlocked-date">
                    Unlocked: {new Date(userAchievement.unlockedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Achievement Detail Modal */}
      {selectedAchievement && (
        <div className="modal-overlay" onClick={() => setSelectedAchievement(null)}>
          <div className="achievement-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedAchievement(null)}>
              ×
            </button>

            <div className="modal-header" style={{ background: getTierGradient(selectedAchievement.achievement.tier) }}>
              <div className="modal-icon">{selectedAchievement.completed ? selectedAchievement.achievement.icon : '🔒'}</div>
              <h2>
                {selectedAchievement.achievement.name}
                {!selectedAchievement.completed && <span className="locked-badge"> 🔒 Locked</span>}
              </h2>
              <div className="modal-tier" style={{ color: getTierColor(selectedAchievement.achievement.tier) }}>
                {selectedAchievement.achievement.tier.toUpperCase()}
              </div>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                {selectedAchievement.achievement.description}
                {!selectedAchievement.completed && (
                  <span className="unlock-hint">
                    <br /><br />
                    <strong>How to unlock:</strong> Complete the above requirement to earn ⭐ {selectedAchievement.achievement.points} points!
                  </span>
                )}
              </p>

              {selectedAchievement.completed ? (
                <div className="modal-stats">
                  <div className="modal-stat">
                    <span className="modal-stat-label">Points Earned:</span>
                    <span className="modal-stat-value">⭐ {selectedAchievement.achievement.points}</span>
                  </div>
                  {selectedAchievement.unlockedAt && (
                    <div className="modal-stat">
                      <span className="modal-stat-label">Unlocked On:</span>
                      <span className="modal-stat-value">
                        {new Date(selectedAchievement.unlockedAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="modal-progress">
                  <div className="modal-progress-header">
                    <span>Progress</span>
                    <span>{selectedAchievement.progressPercentage}%</span>
                  </div>
                  <div className="modal-progress-bar">
                    <div
                      className="modal-progress-fill"
                      style={{
                        width: `${selectedAchievement.progressPercentage}%`,
                        background: getTierColor(selectedAchievement.achievement.tier),
                      }}
                    ></div>
                  </div>
                  <div className="modal-progress-text">
                    {selectedAchievement.progress} / {selectedAchievement.target}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsPage;
